import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  User, Briefcase, DollarSign, Clock, FileText, Download,
  CheckCircle, AlertCircle, LogOut, ChevronDown, ChevronUp, UserCircle,
  Shield, ScrollText, Users, FileCheck, MessageCircle, Send, HelpCircle,
  Eye, Lock, Upload, X, Loader2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreJoiningDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedPolicies, setExpandedPolicies] = useState({});
  const [acknowledging, setAcknowledging] = useState({});
  const [newQuestion, setNewQuestion] = useState("");
  const [askingQuestion, setAskingQuestion] = useState(false);
  
  // Offer Letter State
  const [offerData, setOfferData] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [requestingOtp, setRequestingOtp] = useState(false);
  const [acceptingOffer, setAcceptingOffer] = useState(false);
  
  // Document Upload State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "", category: "", file: null });
  const [myDocuments, setMyDocuments] = useState([]);

  const candidateToken = localStorage.getItem("candidateToken");

  const fetchDashboard = useCallback(async () => {
    if (!candidateToken) {
      navigate("/prejoining");
      return;
    }

    try {
      const [dashboardRes, offerRes, docsRes] = await Promise.all([
        axios.get(`${API}/prejoining/dashboard`, {
          headers: { Authorization: `Bearer ${candidateToken}` }
        }),
        axios.get(`${API}/prejoining/offer-letter`, {
          headers: { Authorization: `Bearer ${candidateToken}` }
        }),
        axios.get(`${API}/prejoining/my-documents`, {
          headers: { Authorization: `Bearer ${candidateToken}` }
        })
      ]);
      setDashboardData(dashboardRes.data);
      setOfferData(offerRes.data);
      setMyDocuments(docsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("candidateToken");
        localStorage.removeItem("candidateInfo");
        toast.error("Session expired. Please login again.");
        navigate("/prejoining");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [candidateToken, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("candidateToken");
    localStorage.removeItem("candidateInfo");
    navigate("/prejoining");
    toast.success("Logged out successfully");
  };

  const togglePolicy = (key) => {
    setExpandedPolicies(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/prejoining/document/${doc.id}`, {
        headers: { Authorization: `Bearer ${candidateToken}` }
      });
      
      const linkSource = `data:${response.data.file_type};base64,${response.data.file_data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.original_filename || doc.file_name;
      downloadLink.click();
      
      toast.success(`Downloaded: ${doc.file_name}`);
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  const handleDownloadAcceptedOffer = async () => {
    try {
      toast.loading("Preparing your accepted offer letter...");
      const response = await axios.get(`${API}/prejoining/download-accepted-offer`, {
        headers: { Authorization: `Bearer ${candidateToken}` }
      });
      
      const linkSource = `data:${response.data.file_type};base64,${response.data.file_data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.file_name;
      downloadLink.click();
      
      toast.dismiss();
      toast.success("Accepted offer letter downloaded with acceptance stamp!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to download accepted offer letter");
    }
  };

  const handleAcknowledgePolicy = async (policyKey) => {
    if (acknowledging[policyKey]) return;
    
    setAcknowledging({ ...acknowledging, [policyKey]: true });
    try {
      await axios.post(`${API}/prejoining/acknowledge-policy`, 
        { policy_key: policyKey },
        { headers: { Authorization: `Bearer ${candidateToken}` } }
      );
      
      toast.success("Policy acknowledged!");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to acknowledge policy");
    } finally {
      setAcknowledging({ ...acknowledging, [policyKey]: false });
    }
  };

  const handleAskQuestion = async () => {
    if (!newQuestion.trim() || askingQuestion) return;
    
    setAskingQuestion(true);
    try {
      await axios.post(`${API}/prejoining/questions`,
        { question: newQuestion.trim() },
        { headers: { Authorization: `Bearer ${candidateToken}` } }
      );
      
      toast.success("Question submitted! HR will respond soon.");
      setNewQuestion("");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to submit question");
    } finally {
      setAskingQuestion(false);
    }
  };

  // Offer Letter Functions
  const handleRequestOtp = async () => {
    setRequestingOtp(true);
    try {
      await axios.post(`${API}/prejoining/request-otp`, {}, {
        headers: { Authorization: `Bearer ${candidateToken}` }
      });
      toast.success("OTP sent to your email!");
      setShowOfferModal(false);
      setShowOtpModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setRequestingOtp(false);
    }
  };

  const handleAcceptOffer = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter 6-digit OTP");
      return;
    }
    
    setAcceptingOffer(true);
    try {
      await axios.post(`${API}/prejoining/accept-offer`, 
        { otp },
        { headers: { Authorization: `Bearer ${candidateToken}` } }
      );
      toast.success("Offer accepted successfully! Check your email for confirmation.");
      setShowOtpModal(false);
      setOtp("");
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept offer");
    } finally {
      setAcceptingOffer(false);
    }
  };

  // Document Upload Functions
  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.name || !uploadForm.category) {
      toast.error("Please fill all fields");
      return;
    }
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append("document_name", uploadForm.name);
    formData.append("document_category", uploadForm.category);
    formData.append("file", uploadForm.file);
    
    try {
      await axios.post(`${API}/prejoining/upload-document`, formData, {
        headers: { 
          Authorization: `Bearer ${candidateToken}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded successfully!");
      setShowUploadModal(false);
      setUploadForm({ name: "", category: "", file: null });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="font-dm text-charcoal">Failed to load dashboard</p>
          <button 
            onClick={() => navigate("/prejoining")}
            className="mt-4 text-gold hover:underline font-dm"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const { candidate, document_checklist, policies, policy_acknowledgements, documents, questions, offer_status, offer_accepted_at } = dashboardData;

  // Calculate progress
  const totalPolicies = Object.keys(policies).length;
  const acknowledgedPolicies = Object.keys(policy_acknowledgements || {}).length;
  const allAcknowledged = totalPolicies > 0 && acknowledgedPolicies >= totalPolicies;

  const getDocStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700"
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="prejoining-dashboard">
      {/* Header */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-navy flex items-center justify-center">
                <span className="font-playfair font-bold text-white text-lg">CI</span>
              </div>
              <div className="flex flex-col">
                <span className="font-playfair font-bold text-navy text-lg leading-tight">Creative</span>
                <span className="font-dm text-xs text-gold tracking-wider">PRE-JOINING PORTAL</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-dm text-sm text-charcoal">{candidate.name}</p>
                <p className="font-dm text-xs text-gray-500">{candidate.position}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-dm text-sm"
                data-testid="prejoining-logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-navy to-navy/90 rounded-2xl p-8 mb-8 text-white"
        >
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-10 h-10 text-gold" />
            </div>
            <div className="flex-1">
              <h1 className="font-playfair text-2xl md:text-3xl mb-2">
                Welcome, {candidate.name}!
              </h1>
              <p className="font-dm text-white/80 text-sm md:text-base mb-4">
                Please review your offer letter, read each policy carefully and acknowledge them. Upload any required documents.
              </p>
              {/* Progress Bar */}
              <div className="bg-white/20 rounded-full h-3 max-w-md">
                <div 
                  className="bg-gold rounded-full h-3 transition-all duration-500"
                  style={{ width: `${totalPolicies > 0 ? (acknowledgedPolicies / totalPolicies) * 100 : 0}%` }}
                />
              </div>
              <p className="font-dm text-white/70 text-xs mt-2">
                {acknowledgedPolicies} of {totalPolicies} policies acknowledged
              </p>
            </div>
          </div>
        </motion.div>

        {/* Offer Letter Card - Priority */}
        {offerData?.has_offer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className={`rounded-xl border overflow-hidden mb-8 ${
              offerData.offer_accepted ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
            }`}
          >
            <div className={`px-6 py-4 border-b ${
              offerData.offer_accepted ? 'bg-green-100 border-green-200' : 'bg-amber-100 border-amber-200'
            }`}>
              <h2 className={`font-playfair text-lg flex items-center gap-2 ${
                offerData.offer_accepted ? 'text-green-800' : 'text-amber-800'
              }`}>
                <FileText className="w-5 h-5" />
                Offer Letter
              </h2>
            </div>
            <div className="p-6">
              {offerData.offer_accepted ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-dm font-medium text-green-800">Offer Accepted</p>
                      <p className="font-dm text-xs text-green-600">
                        Accepted on {new Date(offer_accepted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDownloadAcceptedOffer}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-dm text-sm hover:bg-green-700"
                    data-testid="download-accepted-offer-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download Signed Letter
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-dm font-medium text-amber-800">Your offer letter is ready</p>
                      <p className="font-dm text-xs text-amber-600">
                        View and accept your offer to download
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOfferModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-dm text-sm hover:bg-amber-700"
                    data-testid="view-offer-letter-btn"
                  >
                    <Eye className="w-4 h-4" />
                    View Offer Letter
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Offer Details & Documents */}
          <div className="lg:col-span-1 space-y-6">
            {/* Offer Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-gold" />
                  Offer Details
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-dm text-xs text-gray-500 uppercase tracking-wide">Position</p>
                    <p className="font-dm text-charcoal font-medium">{candidate.position}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-dm text-xs text-gray-500 uppercase tracking-wide">Salary</p>
                    <p className="font-dm text-charcoal font-medium">{candidate.salary}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-dm text-xs text-gray-500 uppercase tracking-wide">Probation Period</p>
                    <p className="font-dm text-charcoal font-medium">{candidate.probation_period}</p>
                  </div>
                </div>

                {candidate.reporting_manager && (
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-dm text-xs text-gray-500 uppercase tracking-wide">Reporting Manager</p>
                      <p className="font-dm text-charcoal font-medium">{candidate.reporting_manager}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Document Checklist */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-gold" />
                  Documents to Bring
                </h2>
              </div>
              <div className="p-6">
                <p className="font-dm text-sm text-gray-500 mb-4">
                  Please bring the following documents on your joining date:
                </p>
                <ul className="space-y-2">
                  {document_checklist.map((doc, index) => (
                    <li key={index} className="flex items-center gap-3 font-dm text-sm text-charcoal">
                      <span className="w-2 h-2 bg-gold rounded-full" />
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* My Uploaded Documents */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                  <Upload className="w-5 h-5 text-gold" />
                  My Documents
                </h2>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="text-gold hover:text-gold/80 font-dm text-sm flex items-center gap-1"
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4" /> Upload
                </button>
              </div>
              <div className="p-6">
                {myDocuments.length === 0 ? (
                  <p className="font-dm text-sm text-gray-500 text-center py-4">
                    No documents uploaded yet. Click "Upload" to add documents.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {myDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-dm text-sm text-charcoal">{doc.document_name}</p>
                          <p className="font-dm text-xs text-gray-500">{doc.document_category}</p>
                        </div>
                        {getDocStatusBadge(doc.status)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Downloadable Documents */}
            {documents && documents.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                  <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                    <Download className="w-5 h-5 text-gold" />
                    Company Documents
                  </h2>
                </div>
                <div className="p-6 space-y-3">
                  {documents.map((doc) => (
                    <div 
                      key={doc.id}
                      className="border border-gray-200 rounded-lg p-3 hover:border-gold/50 hover:bg-gold/5 transition-all cursor-pointer group"
                      onClick={() => handleDownload(doc)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-navy group-hover:text-gold" />
                        <div className="flex-1 min-w-0">
                          <p className="font-dm text-sm text-charcoal font-medium truncate">{doc.file_name}</p>
                        </div>
                        <Download className="w-5 h-5 text-gray-400 group-hover:text-gold" />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Policies & Q&A */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Policies */}
            {Object.keys(policies).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                  <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                    <ScrollText className="w-5 h-5 text-gold" />
                    Company Policies
                  </h2>
                  <p className="font-dm text-xs text-gray-500 mt-1">Read each policy carefully and acknowledge</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {Object.entries(policies).map(([key, policy]) => {
                    const isAcknowledged = policy_acknowledgements?.[key]?.acknowledged;
                    const isExpanded = expandedPolicies[key];
                    
                    return (
                      <div key={key} className="group">
                        <button
                          onClick={() => togglePolicy(key)}
                          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                          data-testid={`policy-toggle-${key}`}
                        >
                          <div className="flex items-center gap-3">
                            {isAcknowledged ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <Shield className="w-5 h-5 text-gray-400" />
                            )}
                            <span className={`font-dm font-medium ${isAcknowledged ? 'text-green-700' : 'text-charcoal'}`}>
                              {policy.title}
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-6 pb-6">
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <div className="font-dm text-sm text-charcoal-muted leading-relaxed whitespace-pre-line">
                                {policy.content}
                              </div>
                            </div>
                            
                            {isAcknowledged ? (
                              <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-dm text-sm">
                                  Acknowledged on {new Date(policy_acknowledgements[key].timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleAcknowledgePolicy(key)}
                                disabled={acknowledging[key]}
                                className="w-full bg-gold hover:bg-gold/90 text-white font-dm font-medium py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                data-testid={`acknowledge-policy-${key}`}
                              >
                                {acknowledging[key] ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="w-5 h-5" />
                                    I Have Read & Understood This Policy
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Completion Status */}
            {allAcknowledged && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-xl p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl text-green-800 mb-1">All Policies Acknowledged!</h3>
                    <p className="font-dm text-sm text-green-700">
                      Thank you for reviewing and acknowledging all company policies.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Q&A Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h2 className="font-playfair text-lg text-charcoal flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-gold" />
                  Questions & Support
                </h2>
              </div>
              <div className="p-6">
                {/* Ask Question Form */}
                <div className="mb-6">
                  <textarea
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    placeholder="Type your question here..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold resize-none"
                  />
                  <button
                    onClick={handleAskQuestion}
                    disabled={!newQuestion.trim() || askingQuestion}
                    className="mt-3 bg-navy hover:bg-navy/90 text-white font-dm font-medium py-2 px-6 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {askingQuestion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit Question
                  </button>
                </div>

                {/* Previous Questions */}
                {questions && questions.length > 0 && (
                  <div>
                    <h3 className="font-dm font-medium text-charcoal mb-4">Your Questions</h3>
                    <div className="space-y-4">
                      {questions.map((q) => (
                        <div key={q.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-4 py-3">
                            <div className="flex items-start gap-3">
                              <MessageCircle className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-dm text-sm text-charcoal">{q.question}</p>
                                <p className="font-dm text-xs text-gray-500 mt-1">
                                  {new Date(q.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {q.reply ? (
                            <div className="bg-green-50 px-4 py-3 border-t border-gray-200">
                              <div className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                <div>
                                  <p className="font-dm text-xs text-green-700 mb-1">
                                    Reply from {q.replied_by}
                                  </p>
                                  <p className="font-dm text-sm text-green-800">{q.reply}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="px-4 py-3 border-t border-gray-200">
                              <p className="font-dm text-xs text-amber-600 flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Awaiting response from HR
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Offer Letter View Modal */}
      {showOfferModal && offerData && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          >
            <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-playfair text-xl">Offer Letter - Protected View</h2>
              <button onClick={() => setShowOfferModal(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Watermark Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-dm font-medium text-amber-800">Protected Document</p>
                    <p className="font-dm text-sm text-amber-700">
                      This document is watermarked. Download will be available after you accept the offer.
                    </p>
                  </div>
                </div>
              </div>

              {/* PDF Viewer */}
              <div 
                className="relative rounded-lg overflow-hidden mb-6 border border-gray-200"
                style={{ userSelect: 'none' }}
                onContextMenu={(e) => e.preventDefault()}
              >
                {offerData.file_data ? (
                  <div className="relative">
                    <iframe
                      src={`data:application/pdf;base64,${offerData.file_data}#toolbar=0&navpanes=0&scrollbar=1`}
                      className="w-full h-[500px]"
                      title="Offer Letter"
                      style={{ pointerEvents: offerData.offer_accepted ? 'auto' : 'auto' }}
                    />
                    {/* Watermark Overlay - only show if not accepted */}
                    {!offerData.offer_accepted && (
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="absolute inset-0 bg-repeat opacity-[0.08]" style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='150'%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%231E3A5F' text-anchor='middle' transform='rotate(-30 150 75)'%3ECONFIDENTIAL - ${encodeURIComponent(offerData.watermark_text)}%3C/text%3E%3C/svg%3E")`,
                        }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-100 h-[500px] flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="font-dm text-gray-600">Loading document...</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Accept Button */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowOfferModal(false)}
                  className="px-6 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handleRequestOtp}
                  disabled={requestingOtp}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-dm text-sm hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                  data-testid="accept-offer-btn"
                >
                  {requestingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Accept Offer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl w-full max-w-md overflow-hidden"
          >
            <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-playfair text-xl">Verify OTP</h2>
              <button onClick={() => setShowOtpModal(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="font-dm text-sm text-gray-600 mb-6">
                We've sent a 6-digit OTP to your email. Enter it below to accept the offer.
              </p>
              
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-mono text-center text-2xl tracking-widest focus:outline-none focus:border-gold"
                maxLength={6}
                data-testid="otp-input"
              />
              
              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleRequestOtp}
                  disabled={requestingOtp}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                >
                  Resend OTP
                </button>
                <button
                  onClick={handleAcceptOffer}
                  disabled={acceptingOffer || otp.length !== 6}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-dm text-sm hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="verify-otp-btn"
                >
                  {acceptingOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Confirm
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl w-full max-w-md overflow-hidden"
          >
            <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-playfair text-xl">Upload Document</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleDocumentUpload} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Document Name *</label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                    placeholder="e.g., Aadhaar Card"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                  />
                </div>
                
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Category *</label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Identity Proof">Identity Proof</option>
                    <option value="Address Proof">Address Proof</option>
                    <option value="Educational Certificate">Educational Certificate</option>
                    <option value="Experience Certificate">Experience Certificate</option>
                    <option value="Bank Details">Bank Details</option>
                    <option value="Photo">Photo</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">File (PDF only) *</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    className="w-full text-sm"
                    required
                  />
                </div>
              </div>
              
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingDoc}
                  className="flex-1 px-4 py-2 bg-gold text-white rounded-lg font-dm text-sm hover:bg-gold/90 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="submit-upload-btn"
                >
                  {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 mt-12 py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="font-dm text-sm text-gray-500">
            Need immediate assistance? Contact HR at{" "}
            <a href="mailto:info@creativeinfotech.net" className="text-gold hover:underline">
              info@creativeinfotech.net
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PreJoiningDashboard;
